/**
 * O QueryClient, com as regras duras de carga escritas como configuração —
 * não como boa intenção.
 *
 * O motivo é um fato, não uma preferência: o servidor de banco está no limite e
 * a migração para o novo vem antes de qualquer ambiente de desenvolvimento. Até
 * lá, o front não pode acrescentar carga. Os defaults do TanStack Query são
 * exatamente o contrário do que este banco aguenta — `refetchOnWindowFocus` vem
 * ligado de fábrica, e alternar entre o navegador e a planilha dispararia uma
 * rodada de consultas a cada volta.
 *
 * Se algum dia uma tela precisar quebrar uma destas, a conversa é sobre a
 * regra — e ela está na seção 3 do plano de execução.
 */

import { QueryClient } from "@tanstack/react-query"
import { ErroDaApi } from "./erros"

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Regra 4 — nada revalida ao voltar o foco na aba.
      refetchOnWindowFocus: false,
      // ... nem ao reconectar, nem ao remontar o componente.
      refetchOnReconnect: false,
      refetchOnMount: false,
      // Regra 3 — nenhuma tela faz polling.
      refetchInterval: false,

      // Cinco minutos de frescor para o caso geral; catálogo sobrescreve para
      // infinito na sua própria chave (regra 1).
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,

      // Repetir consulta é dobrar carga. Erro de servidor não se resolve
      // tentando de novo; erro de rede pode, uma vez só.
      retry: (tentativa, erro) => {
        if (erro instanceof ErroDaApi && erro.status > 0) return false
        return tentativa < 1
      },
    },
    mutations: {
      // Escrita nunca se repete sozinha: contra este banco, uma inclusão
      // repetida é uma linha duplicada em produção.
      retry: false,
    },
  },
})

/**
 * Regra 1 — catálogo carrega uma vez por projeto, por sessão.
 * Trocar de projeto troca a chave; voltar ao mesmo projeto não gera requisição.
 */
export const catalogoPorProjeto = {
  staleTime: Infinity,
  gcTime: Infinity,
} as const

/** Regra 2 — busca no servidor só a partir de 2 caracteres, com 400 ms de espera. */
export const BUSCA_MINIMO_CARACTERES = 2
export const BUSCA_ESPERA_MS = 400

/** A API pagina em 200. O front não antecipa a página seguinte (regra 5). */
export const POR_PAGINA = 200

export const chaves = {
  projetos: (filtro?: unknown) => ["projetos", filtro ?? null] as const,
  projeto: (id: number) => ["projeto", id] as const,

  componentes: (projetoId: number) => ["catalogo", "componentes", projetoId] as const,
  conectores: (projetoId: number, componenteId?: number) =>
    ["catalogo", "conectores", projetoId, componenteId ?? null] as const,
  circuitos: (projetoId: number, termo: string) => ["catalogo", "circuitos", projetoId, termo] as const,
  cores: (projetoId: number) => ["catalogo", "cores", projetoId] as const,
  chicotes: (projetoId: number) => ["catalogo", "chicotes", projetoId] as const,
  restricoes: (projetoId: number) => ["catalogo", "restricoes", projetoId] as const,

  pinouts: (projetoId: number, filtros: unknown, pagina: number) =>
    ["pinouts", projetoId, filtros, pagina] as const,

  acessos: () => ["acessos"] as const,
}
